import { useApolloClient, useQuery } from "@apollo/react-hooks";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useParams } from "react-router";
import { useToasts } from "react-toast-notifications";
import { openNcfAffiliateAnnexReport } from "../../reports/ncfAffiliateAnnexReport";
import { openNcfBranchAnnexReport } from "../../reports/ncfBranchAnnexReport";
import { POPUP_BLOCKED_MESSAGE, tryOpenReport } from "../../reports/openReport";
import {
  getBillingReportData,
  getBillingReportWhere,
  getConsultoryReportData,
  getLogoDataUrl,
  getMonthlyClosingPeriod,
  printNcfPrincipalReport,
} from "../../reports/reportContext";
import { getGraphQLErrorMessage } from "@dhn/shared/utils";
import {
  GET_BILLINGS_BY_PERIOD,
  GET_CONSULTORY_AFFILIATE_REPORT_BY_PERIOD,
  GET_CONSULTORY_BRANCH_REPORT_BY_PERIOD,
  GET_MONTHLY_CLOSING_BY_ID,
} from "./queries";

const INITIAL_STATE = {
  monthlyClosing: null,
  sales: [],
  selectedDate: null,
  selectedBilling: null,
  isOpenCreditNoteModal: false,
  isReprinting: false,
};

export default function useActions({ dataDoctor }) {
  const [state, setState] = useState(INITIAL_STATE);
  const client = useApolloClient();
  const { params } = useParams();
  const { addToast } = useToasts();

  const monthlyClosingId = useMemo(() => parseInt(params), [params]);

  const {
    data: { getConsultoryMonthlyClosure } = {},
    loading: loadingMonthlyClosing,
  } = useQuery(GET_MONTHLY_CLOSING_BY_ID, {
    skip: Number.isNaN(monthlyClosingId),
    fetchPolicy: "network-only",
    variables: {
      where: {
        id: monthlyClosingId,
        medicalStaffId: dataDoctor?.medicalStaffId,
        branchOfficeId: dataDoctor?.branchOfficeId,
      },
      relations: ["status", "pharmacyProvider", "pharmacyProvider.sector"],
    },
  });

  const monthlyClosing = getConsultoryMonthlyClosure?.[0];

  const {
    data: { getConsultoryMonthlyClosedDetail = [] } = {},
    loading: loadingClosedDetail,
    refetch: refetchClosedDetail,
  } = useQuery(GET_BILLINGS_BY_PERIOD, {
    skip: !monthlyClosingId || !dataDoctor?.medicalStaffId,
    fetchPolicy: "network-only",
    variables: {
      where: {
        monthlyClosingId,
        medicalStaffId: dataDoctor?.medicalStaffId,
        branchOfficeId: dataDoctor?.branchOfficeId,
      },
    },
  });

  const groupedSales = useMemo(
    () => getConsultoryMonthlyClosedDetail || [],
    [getConsultoryMonthlyClosedDetail],
  );

  useEffect(() => {
    if (!loadingMonthlyClosing && monthlyClosing) {
      setState((prev) => ({
        ...prev,
        monthlyClosing,
        selectedDate: moment(monthlyClosing.date).toDate(),
      }));
    }
  }, [loadingMonthlyClosing, monthlyClosing]);

  useEffect(() => {
    if (!loadingClosedDetail) {
      setState((prev) => {
        const nextState = {
          ...prev,
          sales: groupedSales,
        };

        if (prev?.isOpenCreditNoteModal && prev?.selectedBilling?.id) {
          const refreshedSelectedBilling = groupedSales.find(
            (item) => item?.id === prev?.selectedBilling?.id,
          );

          if (refreshedSelectedBilling) {
            nextState.selectedBilling = {
              ...refreshedSelectedBilling,
              monthlyClosingId,
            };
          }
        }

        return nextState;
      });
    }
  }, [loadingClosedDetail, groupedSales, monthlyClosingId]);

  const onChange = (key, value) => {
    setState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const onOpenCreditNote = async (selectedBilling) => {
    let refreshedSelectedBilling = selectedBilling;

    try {
      const refreshed = await refetchClosedDetail();
      const freshRows = refreshed?.data?.getConsultoryMonthlyClosedDetail || [];

      refreshedSelectedBilling =
        freshRows.find((item) => item?.id === selectedBilling?.id) ||
        freshRows.find(
          (item) =>
            item?.arsId === selectedBilling?.arsId &&
            (item?.ncf || "") === (selectedBilling?.ncf || ""),
        ) ||
        selectedBilling;
    } catch (error) {
      // Si el refetch falla, se usa la fila actual para no bloquear el modal.
    }

    onChange("selectedBilling", {
      ...refreshedSelectedBilling,
      monthlyClosingId,
    });
    onChange("isOpenCreditNoteModal", true);
  };

  const onCloseCreditNote = () => {
    onChange("isOpenCreditNoteModal", false);
    onChange("selectedBilling", null);
  };

  const runReprint = async (selectedBilling, successMessage, printReport) => {
    if (!selectedBilling?.firstSaleId) {
      addToast("No existe una venta para re-imprimir", {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }

    if (state.isReprinting) {
      return;
    }

    try {
      onChange("isReprinting", true);

      const opened = await printReport({
        logoDataUrl: getLogoDataUrl(),
        where: getBillingReportWhere({
          monthlyClosingId,
          dataDoctor,
          billing: selectedBilling,
        }),
      });

      addToast(opened ? successMessage : POPUP_BLOCKED_MESSAGE, {
        appearance: opened ? "success" : "warning",
        autoDismiss: true,
      });
    } catch (error) {
      addToast(
        getGraphQLErrorMessage(error, "No fue posible generar la reimpresion"),
        {
          appearance: "error",
          autoDismiss: true,
        },
      );
    } finally {
      onChange("isReprinting", false);
    }
  };

  const getAnnexReportData = (selectedBilling, logoDataUrl) => ({
    logoDataUrl,
    ...getConsultoryReportData(monthlyClosing),
    ...getBillingReportData(selectedBilling),
    ...getMonthlyClosingPeriod(monthlyClosing),
  });

  const handleReprintNcfPrincipal = (selectedBilling) =>
    runReprint(
      selectedBilling,
      "Reimpresion de NCF generada correctamente",
      ({ logoDataUrl, where }) =>
        printNcfPrincipalReport(client, {
          monthlyClosing,
          billing: selectedBilling,
          where,
          logoDataUrl,
        }),
    );

  const handleReprintAffiliateAnnex = (selectedBilling) =>
    runReprint(
      selectedBilling,
      "Reimpresion de Anexo 1 generada correctamente",
      async ({ logoDataUrl, where }) => {
        const affiliateResult = await client.query({
          query: GET_CONSULTORY_AFFILIATE_REPORT_BY_PERIOD,
          variables: { where },
          fetchPolicy: "network-only",
        });

        return tryOpenReport(openNcfAffiliateAnnexReport, {
          ...getAnnexReportData(selectedBilling, logoDataUrl),
          processedRows:
            affiliateResult?.data?.getConsultoryMonthlyAffiliateReport || [],
          canceledRows: [],
        });
      },
    );

  const handleReprintBranchAnnex = (selectedBilling) =>
    runReprint(
      selectedBilling,
      "Reimpresion de Anexo 2 generada correctamente",
      async ({ logoDataUrl, where }) => {
        const branchResult = await client.query({
          query: GET_CONSULTORY_BRANCH_REPORT_BY_PERIOD,
          variables: { where },
          fetchPolicy: "network-only",
        });

        return tryOpenReport(openNcfBranchAnnexReport, {
          ...getAnnexReportData(selectedBilling, logoDataUrl),
          rows: branchResult?.data?.getConsultoryMonthlyBranchReport || [],
        });
      },
    );

  return [
    { state, loadingMonthlyClosing, loadingClosedDetail },
    {
      onOpenCreditNote,
      onCloseCreditNote,
      handleReprintNcfPrincipal,
      handleReprintAffiliateAnnex,
      handleReprintBranchAnnex,
      refetchClosedDetail,
    },
  ];
}
